import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { UserRound } from "lucide-react";
import { usePrescriptionFlowSend } from "../context";
import { KioskStep } from "../../../../../components/kiosk-step";
import { usePatientLookup } from "../../scheduler/identification/patient-lookup";
import {
  KioskLookupNotFound,
  KioskPatientLookupPanel,
  type KioskPatientLookupTestIds,
} from "../../scheduler/identification/KioskPatientLookupPanel";
import { PrescriptionIdentificationTabs } from "./PrescriptionIdentificationTabs";

const PRESCRIPTION_LOOKUP_ID_PREFIX = "prescription-lookup";

const PRESCRIPTION_LOOKUP_TEST_IDS: KioskPatientLookupTestIds = {
  form: "frontdesk-prescription-patient-lookup-form",
  firstName: "frontdesk-prescription-patient-lookup-firstname-input",
  lastName: "frontdesk-prescription-patient-lookup-lastname-input",
  dateOfBirth: "frontdesk-prescription-patient-lookup-dob-input",
  dateOfBirthPicker: "frontdesk-prescription-patient-lookup-dob-picker-btn",
  search: "frontdesk-prescription-patient-lookup-search-btn",
  confirm: "frontdesk-prescription-patient-lookup-confirm-btn",
  found: "frontdesk-prescription-patient-lookup-found-card",
  ambiguous: "frontdesk-prescription-patient-lookup-ambiguous-card",
  notFound: "frontdesk-prescription-patient-lookup-not-found-card",
  error: "frontdesk-prescription-patient-lookup-error-card",
};

/**
 * Name/DOB fallback patient lookup step for the prescription flow, built on
 * the lookup shared with the scheduler (KO-07, FC-07): same title, tabs,
 * field order, wording and footer actions.
 *
 * @since 1.0.0
 * @example
 * ```tsx
 * <PatientLookupStep />
 * ```
 */
export function PatientLookupStep() {
  const { t } = useTranslation();
  const send = usePrescriptionFlowSend();
  const lookup = usePatientLookup({ component: "PrescriptionPatientLookup" });

  useEffect(function focusPrescriptionPatientLookupLastName() {
    document.getElementById(`${PRESCRIPTION_LOOKUP_ID_PREFIX}-lastname`)?.focus();
  }, []);

  return (
    <KioskStep
      width="narrow"
      title={t("self-service.identification.title")}
      lead={t("self-service.identification.lookup.lead")}
    >
      <PrescriptionIdentificationTabs
        activeMode="search"
        onCard={() => send({ type: "BACK" })}
        onSearch={() => undefined}
      />
      <KioskPatientLookupPanel
        lookup={lookup}
        idPrefix={PRESCRIPTION_LOOKUP_ID_PREFIX}
        testIds={PRESCRIPTION_LOOKUP_TEST_IDS}
        onConfirm={({ patientId, insurance }) =>
          send({
            type: "MANUAL_PATIENT_FOUND",
            patientId,
            insuranceType: insurance === "GKV" ? "PUBLIC" : "PRIVATE",
          })
        }
        notFound={
          <KioskLookupNotFound
            testId={PRESCRIPTION_LOOKUP_TEST_IDS.notFound}
            action={{
              label: t("self-service.help-reception"),
              icon: UserRound,
              onClick: () => send({ type: "NO_PATIENT_FOUND" }),
              testId: "frontdesk-prescription-patient-lookup-not-found-btn",
            }}
          />
        }
      />
    </KioskStep>
  );
}
